import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import React, { useCallback } from "react";

import { css } from "@emotion/react";
import { CSS_MQ } from "../../../styles/breakpoints";
import { FONTS, fontSize } from "../../../styles/fonts";
import { VideoIconsRenderer } from "../video-icons";
import { NumericalProgress } from "./NumericalProgress";
import { LinearScale } from "./LinearScale";
import { CSS_COLORS } from "../../../styles/styles-css-variables";

export const VideoControlsLowerBar: ReactTFC<{
  paused: boolean;
  duration: number | undefined;
  soundLevel: number | undefined;
  currentTime: number | undefined;
  setSoundLevelTo: (v: number) => void;
  togglePlayMode: () => void;
  setProgressTo: (p: number) => void;
  captions: "not-available" | "visible" | "hidden";
  toggleCaptions: () => void;
}> = props => {
  const scaleRough =
    typeof props.currentTime === "number" && typeof props.duration !== "undefined" && props.duration > 0
      ? props.currentTime / props.duration
      : 0;

  const scale = Number.isFinite(scaleRough) ? scaleRough : 0;

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      const rect = (e.target as any).getBoundingClientRect();

      const width = e.clientX - rect.left;
      const seekProgress = width / rect.width;

      props.setProgressTo(seekProgress);
    },
    [props.setProgressTo],
  );

  const muteCallback = useCallback(() => props.setSoundLevelTo(0), [props.setSoundLevelTo]);
  const soundOnCallback = useCallback(() => props.setSoundLevelTo(1), [props.setSoundLevelTo]);

  const buttonStyles = css`
    display: inline-block;
    height: 100%;
  `;
  return (
    <div
      css={css`
        max-width: var(--max-container-width);
        width: 100%;
        height: 100%;

        display: flex;
        justify-content: flex-start;
        align-items: center;

        color: ${CSS_COLORS.White};

        ${CSS_MQ.atMostSm} {
          padding-left: 6px;
          padding-right: 6px;
        }
      `}
    >
      <button onClick={props.togglePlayMode} css={buttonStyles}>
        <VideoIconsRenderer icon={props.paused ? "PlayBarSvgIcon" : "PauseBarSvgIcon"} />
      </button>

      <div
        css={css`
          display: flex;
          flex-grow: 1;
          align-items: center;

          padding: 10px 0;

          ._progress-bar {
            transition: 0.2s linear transform;
          }

          &:hover {
            ._progress-bar {
              transition: 0.2s linear transform;
              transform: scaleY(2);
              transform-origin: center;
            }
          }
        `}
      >
        <span
          className="_progress-bar"
          css={css`
            flex-grow: 1;

            position: relative;
            height: 24px;
            cursor: pointer;
          `}
          onClick={onClick}
        >
          <span
            css={css`
              pointer-events: none;

              width: 100%;
              height: 2px;
              background-color: ${CSS_COLORS.White};
              position: absolute;
              left: 0;
              top: calc(50% - 1px);
            `}
          />
          <LinearScale scale={scale} paused={props.paused} />
        </span>

        <span
          css={[
            FONTS.SwarovskiFutura500,
            fontSize.x2,
            css`
              padding-left: 0.5em;
              padding-right: 0.5em;

              color: ${CSS_COLORS.White};

              ${CSS_MQ.atMostSm} {
                margin-top: 2px;
                line-height: 1;
              }

              min-width: 10em;
              text-align: right;
            `,
          ]}
        >
          <NumericalProgress duration={props.duration} currentTime={props.currentTime} />
        </span>
      </div>

      <div
        css={css`
          height: 100%;
          ${CSS_MQ.atLeastMd} {
            margin-left: 10px;
            margin-right: 5px;
          }

          .icon {
            padding: 3px 5px;

            ${CSS_MQ.atLeastMd} {
              padding: 5px 10px;
            }
          }
        `}
      >
        {props.captions === "not-available" ? null : (
          <button onClick={props.toggleCaptions} css={buttonStyles}>
            <VideoIconsRenderer
              icon={props.captions === "visible" ? "CaptionsOnSvgIcon" : "CaptionsOffSvgIcon"}
              className={"icon"}
            />
          </button>
        )}

        {props.soundLevel && props.soundLevel > 0.5 ? (
          <button onClick={muteCallback} css={buttonStyles}>
            <VideoIconsRenderer icon={"SoundOnSvgIcon"} className={"icon"} />
          </button>
        ) : (
          <button onClick={soundOnCallback} css={buttonStyles}>
            <VideoIconsRenderer icon={"SoundOffSvgIcon"} className={"icon"} />
          </button>
        )}
      </div>
    </div>
  );
};
